import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CheckIcon,
  Cross2Icon,
  MagnifyingGlassIcon,
} from '@radix-ui/react-icons';
import { ASSETS } from '../../../../constants/assets';
import { suggestionService, mapSuggestedProfileRow } from '../../../../services/suggestionService';
import { useAuthStore } from '../../../../store/authStore';
import type { SuggestionApprovalStatus } from '../../../../types/suggestion';
import { canSendSuggestions, isRelationshipManager } from '../../../../utils/suggestionPermissions';
import type { SuggestionProfile } from './SuggestProfileModal.types';
import './SuggestProfileModal.scss';

export type { SuggestionProfile } from './SuggestProfileModal.types';

interface SuggestProfileModalProps {
  isOpen: boolean;
  targetUserId: string;
  clientName: string;
  onClose: () => void;
  onSent?: (result: { approvalStatus: SuggestionApprovalStatus }) => void;
}

export const SuggestProfileModal: React.FC<SuggestProfileModalProps> = ({
  isOpen,
  targetUserId,
  clientName,
  onClose,
  onSent,
}) => {
  const role = useAuthStore((state) => state.role);
  const canSend = canSendSuggestions(role);
  const isRm = isRelationshipManager(role);
  const sendButtonLabel = isRm ? 'Submit for approval' : 'Send profile';
  const sendingLabel = isRm ? 'Submitting…' : 'Sending…';

  const [searchQuery, setSearchQuery] = useState('');
  const [profiles, setProfiles] = useState<SuggestionProfile[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [totalCount, setTotalCount] = useState(0);
  const [modalClientName, setModalClientName] = useState(clientName);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchProfiles = useCallback(
    async (search?: string) => {
      if (!targetUserId) return;
      setIsLoading(true);
      setError(null);
      try {
        const response = await suggestionService.searchProfiles(targetUserId, {
          search: search?.trim() || undefined,
          page: 1,
          limit: 10,
        });
        setProfiles(response.data.map(mapSuggestedProfileRow));
        setTotalCount(response.metaData.total);
        const targetName = [
          response.targetUser.firstName,
          response.targetUser.lastName,
        ]
          .filter(Boolean)
          .join(' ');
        if (targetName) setModalClientName(targetName);
      } catch (err) {
        setError((err as { message?: string })?.message ?? 'Failed to load profiles');
        setProfiles([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [targetUserId]
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    setSearchQuery('');
    setSelectedIds(new Set());
    setModalClientName(clientName);
    fetchProfiles();
  }, [isOpen, clientName, fetchProfiles]);

  useEffect(() => {
    if (!isOpen) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchProfiles(searchQuery);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, isOpen, fetchProfiles]);

  const toggleSelect = useCallback((profileId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(profileId)) next.delete(profileId);
      else next.add(profileId);
      return next;
    });
  }, []);

  const handleSend = useCallback(async () => {
    if (selectedIds.size === 0 || !canSend) return;
    setIsSending(true);
    setError(null);
    try {
      const result = await suggestionService.sendSuggestions(targetUserId, {
        suggested_user_ids: Array.from(selectedIds).map(Number),
      });
      onSent?.({ approvalStatus: result.approvalStatus });
      onClose();
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'Failed to send suggestions');
    } finally {
      setIsSending(false);
    }
  }, [canSend, onClose, onSent, selectedIds, targetUserId]);

  if (!isOpen) return null;

  return (
    <div className="suggest-profile-modal" role="presentation">
      <div className="suggest-profile-modal__overlay" onClick={onClose} aria-hidden="true" />

      <section
        className="suggest-profile-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="suggest-profile-title"
      >
        <header className="suggest-profile-modal__header">
          <div>
            <h2 id="suggest-profile-title">Suggest New Profile</h2>
            <p>Available matches for {modalClientName}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <Cross2Icon />
          </button>
        </header>

        {error && <p className="suggest-profile-modal__error">{error}</p>}

        <div className="suggest-profile-modal__toolbar">
          <label className="suggest-profile-modal__search">
            <MagnifyingGlassIcon aria-hidden="true" />
            <input
              type="search"
              placeholder="Search by name or ID..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </label>
        </div>

        <div className="suggest-profile-modal__table-wrap">
          <table className="suggest-profile-modal__table">
            <thead>
              <tr>
                <th>User Profile</th>
                <th>Attributes</th>
                <th>Location &amp; Career</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={4}>Loading profiles…</td>
                </tr>
              ) : profiles.length === 0 ? (
                <tr>
                  <td colSpan={4}>No profiles found.</td>
                </tr>
              ) : (
                profiles.map((profile) => {
                  const isSelected = selectedIds.has(profile.userId);
                  return (
                    <tr key={profile.userId} className={isSelected ? 'is-selected' : ''}>
                      <td>
                        <div className="suggest-profile-modal__profile">
                          <img
                            src={profile.avatar || ASSETS.images.defaultAvatar}
                            alt=""
                          />
                          <div>
                            <strong>{profile.name}</strong>
                            <span>ID: {profile.uniqueId}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span>{profile.meta}</span>
                        <span>{profile.attributes}</span>
                      </td>
                      <td>
                        <span>{profile.location}</span>
                        <span>{profile.career}</span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`suggest-profile-modal__select${
                            isSelected ? ' suggest-profile-modal__select--selected' : ''
                          }`}
                          onClick={() => toggleSelect(profile.userId)}
                        >
                          {isSelected && <CheckIcon aria-hidden="true" />}
                          {isSelected ? 'Selected' : 'Select'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <footer className="suggest-profile-modal__footer">
          <span>
            Showing {profiles.length} of {totalCount} available profiles
          </span>
          <div className="suggest-profile-modal__footer-actions">
            <button type="button" className="suggest-profile-modal__btn suggest-profile-modal__btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="suggest-profile-modal__btn suggest-profile-modal__btn--primary"
              onClick={handleSend}
              disabled={selectedIds.size === 0 || isSending || !canSend}
            >
              {isSending ? sendingLabel : sendButtonLabel}
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
};
